import { useState, useEffect } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { baseUrl } from "../../config/baseUrl"
import empty from "../../assets/images/empty-box.png"
import Swal from "sweetalert2"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTasks,
  faArrowLeft,
  faSave,
  faSearch,
  faClock,
  faQuestionCircle,
  faCheck,
  faSpinner,
  faFilter,
  faBoxOpen,
} from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import Pagination from "../Pagination/Pagination"
import useHorizontalDragScroll from "../../hooks/useHorizontalDragScroll"

export default function AssignTestsToPackage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const dragScrollProps = useHorizontalDragScroll()

  const [packageDetails, setPackageDetails] = useState(null)
  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [savingChanges, setSavingChanges] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Selection state across all pages
  const [assignedTestIds, setAssignedTestIds] = useState(new Set())
  const [initialAssignedIds, setInitialAssignedIds] = useState(new Set())
  const [initialLoaded, setInitialLoaded] = useState(false)

  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("active")
  const [assignedFilter, setAssignedFilter] = useState("") // "", "assigned", "unassigned"
  const [packageFilter, setPackageFilter] = useState("") // selected package ID or "unassigned"
  const [packagesList, setPackagesList] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    limit: 25,
    totalPages: 1,
    totalCount: 0,
  })

  // Load all packages for the dropdown filter
  useEffect(() => {
    const loadPackages = async () => {
      try {
        const res = await axios.get(`${baseUrl}/package`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        })
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setPackagesList(res.data.data)
        }
      } catch (err) {
        console.error("Failed to load packages for dropdown", err)
      }
    }
    loadPackages()
  }, [])

  // Fetch tests whenever filters or page change
  useEffect(() => {
    fetchAssignmentData(currentPage)
  }, [id, currentPage, statusFilter, assignedFilter, packageFilter])

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setCurrentPage(1)
      fetchAssignmentData(1)
    }, 400)
    return () => clearTimeout(handler)
  }, [searchTerm])

  const fetchAssignmentData = async (page = 1) => {
    try {
      setLoading(true)
      const response = await axios.get(`${baseUrl}/test/package-assign-list/${id}`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        params: {
          page,
          limit: pageInfo.limit,
          search: searchTerm.trim(),
          status: statusFilter,
          assignedFilter,
          packageFilter,
        },
      })

      if (response.data.status && response.data.data) {
        const { package: pkg, packages: returnedPkgs, tests: testList, allAssignedIds, pagination } = response.data.data

        if (pkg) setPackageDetails(pkg)
        if (Array.isArray(returnedPkgs) && returnedPkgs.length > 0) {
          setPackagesList(returnedPkgs)
        }
        setTests(testList || [])

        if (pagination) {
          setPageInfo({
            page: pagination.page || 1,
            limit: pagination.limit || 25,
            totalPages: pagination.totalPages || 1,
            totalCount: pagination.totalCount || 0,
          })
        }

        // On first load, populate assigned IDs from backend
        if (!initialLoaded && Array.isArray(allAssignedIds)) {
          const initialSet = new Set(allAssignedIds)
          setAssignedTestIds(new Set(allAssignedIds))
          setInitialAssignedIds(initialSet)
          setInitialLoaded(true)
        }
      } else {
        toast.error("Failed to load assignment data")
      }
    } catch (error) {
      console.error("Error fetching assignment data:", error)
      toast.error(error.response?.data?.message || "Failed to load test series")
    } finally {
      setLoading(false)
    }
  }

  const toggleTestSelection = (testId) => {
    setAssignedTestIds((prev) => {
      const next = new Set(prev)
      if (next.has(testId)) {
        next.delete(testId)
      } else {
        next.add(testId)
      }
      return next
    })
    setHasChanges(true)
  }

  const handleSelectAllVisible = () => {
    const visibleIds = tests.map((t) => t._id)
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((testId) => assignedTestIds.has(testId))

    setAssignedTestIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        visibleIds.forEach((testId) => next.delete(testId))
      } else {
        visibleIds.forEach((testId) => next.add(testId))
      }
      return next
    })
    setHasChanges(true)
  }

  const handleSaveAssignments = async () => {
    try {
      setSavingChanges(true)
      const selectedArray = Array.from(assignedTestIds)

      const response = await axios.post(
        `${baseUrl}/test/assign-to-package`,
        {
          packageId: id,
          testIds: selectedArray,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        }
      )

      if (response.data.status) {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: `Test series assigned successfully! (${selectedArray.length} tests)`,
          timer: 1600,
          showConfirmButton: false,
        })
        setInitialAssignedIds(new Set(selectedArray))
        setHasChanges(false)
        fetchAssignmentData(currentPage)
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: response.data.message || "Failed to assign test series",
        })
      }
    } catch (error) {
      console.error("Error saving assignments:", error)
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.response?.data?.message || "Failed to save assignments",
      })
    } finally {
      setSavingChanges(false)
    }
  }

  return (
    <>
      <section className="main-sec">
        <div className="container-fluid">
          {/* Top Bar / Breadcrumb */}
          <div className="row align-items-center mb-3">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head mb-1">
                  <FontAwesomeIcon icon={faTasks} className="me-2 text-primary" />
                  Assign Test Series to Package
                </h4>
                <div className="custom-bredcump">
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                      <li className="breadcrumb-item">
                        <Link to="/">Dashboard</Link>
                      </li>
                      <li className="breadcrumb-item">
                        <Link to="/test-packages">Test Packages</Link>
                      </li>
                      <li className="breadcrumb-item active" aria-current="page">
                        Assign Tests
                      </li>
                    </ol>
                  </nav>
                </div>
              </div>
            </div>

            <div className="col-lg-6 d-flex justify-content-end align-items-center gap-2">
              <Link to="/test-packages" className="btn btn-outline-secondary px-3">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Packages
              </Link>

              <button
                className="btn btn-success px-4 d-flex align-items-center"
                onClick={handleSaveAssignments}
                disabled={savingChanges || !hasChanges}
              >
                {savingChanges ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save Changes
                    {hasChanges && (
                      <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "10px" }}>
                        Unsaved
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Package Info Card */}
          {packageDetails && (
            <div className="card border-0 shadow-sm rounded-4 mb-4" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)" }}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={`${baseUrl}/${packageDetails.image}` || "/jslogo.png"}
                      alt={packageDetails.title}
                      className="rounded-3 shadow-sm"
                      style={{ width: "64px", height: "64px", objectFit: "cover" }}
                      onError={(e) => {
                        e.target.onError = null
                        e.target.src = "/jslogo.png"
                      }}
                    />
                    <div>
                      <h5 className="mb-1 fw-bold text-dark">{packageDetails.title}</h5>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        {packageDetails.subcategoryID?.title && (
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                            {packageDetails.subcategoryID.title}
                          </span>
                        )}
                        <span className={`badge ${packageDetails.status === "active" ? "bg-success" : "bg-danger"}`}>
                          {packageDetails.status}
                        </span>
                        <span className="text-muted small">
                          Price: ₹{packageDetails.offerPrice || packageDetails.price}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Counters */}
                  <div className="d-flex align-items-center gap-3">
                    <div className="text-center px-3 py-2 bg-white rounded-3 shadow-sm border">
                      <div className="text-muted small fw-semibold">Assigned in Package</div>
                      <div className="fs-5 fw-bold text-success">{assignedTestIds.size}</div>
                    </div>
                    <div className="text-center px-3 py-2 bg-white rounded-3 shadow-sm border">
                      <div className="text-muted small fw-semibold">Total Test Series Available</div>
                      <div className="fs-5 fw-bold text-primary">{pageInfo.totalCount}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Table Card */}
          <div className="cards bus-list">
            <div className="bus-filter">
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <h5 className="card-title mb-1">
                    Select Test Series ({pageInfo.totalCount})
                  </h5>
                  <span className="text-muted small">
                    Check the boxes to assign test series to this package, then click <strong>Save Changes</strong>.
                  </span>
                </div>
                <div className="col-lg-6 d-flex justify-content-end align-items-center gap-2 mt-2 mt-lg-0">
                  <span className="badge bg-primary px-3 py-2 fs-6 rounded-pill">
                    Selected: {assignedTestIds.size}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm px-3"
                    onClick={handleSelectAllVisible}
                    disabled={loading || tests.length === 0}
                  >
                    Select / Deselect Visible Page
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="search-filter-container">
              <div className="search-container">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search test series title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Package filter dropdown */}
              <select
                className="filter-select"
                value={packageFilter}
                onChange={(e) => {
                  setPackageFilter(e.target.value)
                  setCurrentPage(1)
                }}
                style={{ minWidth: "220px", maxWidth: "300px" }}
                title="Filter by Package"
              >
                <option value="">All Packages</option>
                <option value="unassigned">-- No Package Assigned --</option>
                {packagesList.map((pkg) => (
                  <option key={pkg._id} value={pkg._id}>
                    {pkg.title}
                  </option>
                ))}
              </select>

              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setCurrentPage(1)
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <select
                className="filter-select"
                value={assignedFilter}
                onChange={(e) => {
                  setAssignedFilter(e.target.value)
                  setCurrentPage(1)
                }}
              >
                <option value="">All Tests</option>
                <option value="assigned">Already in This Package</option>
                <option value="unassigned">Not in This Package</option>
              </select>
            </div>

            {/* Table */}
            <div className="table-responsive custom-table" {...dragScrollProps}>
              <table className="table table-borderless">
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>SN</th>
                    <th>Test Series Title</th>
                    <th>Questions</th>
                    <th>Duration</th>
                    <th>Marks</th>
                    <th>Status</th>
                    <th className="text-center" style={{ width: "100px" }}>
                      Assign
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <div className="loading-spinner">
                          <div className="spinner border-0"></div>
                        </div>
                        <p className="text-muted mt-2">Loading test series...</p>
                      </td>
                    </tr>
                  ) : tests.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state">
                        <img src={empty || "/placeholder.svg"} alt="No tests" width="180px" />
                        <h4>No Test Series found!</h4>
                        <p className="text-muted">
                          {searchTerm || statusFilter || assignedFilter || packageFilter
                            ? "Try adjusting your search or filter criteria"
                            : "No tests available to assign."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    tests.map((test, index) => {
                      const isAssigned = assignedTestIds.has(test._id)
                      return (
                        <tr
                          key={test._id}
                          onClick={() => toggleTestSelection(test._id)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: isAssigned ? "rgba(16, 185, 129, 0.08)" : undefined,
                            transition: "background-color 0.15s ease",
                          }}
                        >
                          <td>{(pageInfo.page - 1) * pageInfo.limit + index + 1}</td>

                          <td>
                            <div className="fw-semibold text-dark fs-6">{test.title}</div>
                            {Array.isArray(test.packageId) && test.packageId.length > 0 && (
                              <div className="small text-muted mt-1">
                                <span className="fw-semibold">In packages: </span>
                                {test.packageId
                                  .map((p) => p.title || p)
                                  .filter(Boolean)
                                  .join(", ")}
                              </div>
                            )}
                          </td>

                          <td>
                            <span className="badge bg-info-subtle text-info border border-info-subtle px-2 py-1">
                              <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                              {test.questionCount || 0} Questions
                            </span>
                          </td>

                          <td>
                            <span className="text-muted">
                              <FontAwesomeIcon icon={faClock} className="me-1" />
                              {test.duration} mins
                            </span>
                          </td>

                          <td>
                            <span className="fw-semibold text-dark">{test.marks} marks</span>
                          </td>

                          <td>
                            <span className={`status-badge ${test.status === "active" ? "status-active" : "status-inactive"}`}>
                              {test.status === "active" ? (
                                <>
                                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                                  Active
                                </>
                              ) : (
                                "Inactive"
                              )}
                            </span>
                          </td>

                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ width: "22px", height: "22px", cursor: "pointer" }}
                              checked={isAssigned}
                              onChange={() => toggleTestSelection(test._id)}
                            />
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>

              {pageInfo.totalPages > 1 && (
                <Pagination
                  pageNumber={pageInfo.page}
                  totalPages={pageInfo.totalPages}
                  setPageNumber={(newPage) => setCurrentPage(newPage)}
                  totalCount={pageInfo.totalCount}
                  limit={pageInfo.limit}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
